import { Controller, useFormContext } from 'react-hook-form';
// @mui
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
};

export default function RHFTextField({
  name,
  helperText,
  type,
  size = 'small',
  multiline = false,
  sx,
  ...other
}: Props) {
  const { control } = useFormContext();
  const theme = useTheme();

  // Default styling for RHF components
  const defaultSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.neutral',
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'divider',
        borderWidth: '1px',
      },
      '&:hover:not(.Mui-focused):not(.Mui-disabled) fieldset': {
        borderColor: 'text.secondary',
      },
      '&.Mui-focused': {
        backgroundColor: 'background.paper',
        '& fieldset': {
          borderWidth: '1px',
        },
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(255, 72, 66, 0.04)',
        '& fieldset': {
          borderColor: '#ff6b6b',
          borderWidth: '1px',
        },
        '&:hover:not(.Mui-focused) fieldset': {
          borderColor: '#ff5252',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#ff4444',
          borderWidth: '2px',
        },
      },
      '&.Mui-disabled': {
        backgroundColor: 'action.hover',
        '& fieldset': {
          borderColor: 'action.disabled',
          borderStyle: 'dashed',
        },
        '& .MuiInputBase-input': {
          color: 'text.disabled',
          WebkitTextFillColor: 'unset',
        },
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      '&::placeholder': {
        color: 'text.disabled',
        opacity: 0.8,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0,
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
      '&.Mui-error': {
        color: '#d32f2f',
        fontWeight: 400,
      },
      '&:not(.Mui-error)': {
        color: 'text.secondary',
        opacity: 0.8,
      },
    },
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...other}
          name={field.name}
          ref={field.ref}
          fullWidth
          multiline={multiline}
          type={type}
          size={size}
          value={field.value ?? ''}
          onBlur={field.onBlur}
          onChange={(event) => {
            if (type === 'number') {
              const {value} = event.target;
              field.onChange(value === '' ? undefined : Number(value));
            } else {
              field.onChange(event.target.value);
            }
          }}
          error={!!error}
          helperText={error ? error?.message : helperText}
        />
      )}
    />
  );
}
